/* eslint-disable complexity */
/* eslint-disable vue/max-len  */
/* eslint-disable max-len */
import { helpers, required, requiredIf, numeric, integer, minValue, maxValue } from "@vuelidate/validators";
import { useDependantAboutYouStore } from "../stores/dependantAboutYou";
import { useAboutYouStore, MeasurementUnitType, SmokerStatus } from "../stores/aboutYou";
import {
    usePensionOptionsStore,
    PaymentFrequency,
    EscalationType,
    PaymentArrears,
    ValueProtectOrGuarantee,
} from "../stores/pensionOptions";
import { computed } from "vue";
import { parseISO, sub, isAfter, startOfDay } from "date-fns";
import { normalizeText } from ".";
import { PensionFund, useApplicationStore } from "../stores/application";
import { useQuickQuoteCalculatorStore } from "@/stores/quickQuoteCalculator";

const bankValidator = {
    accountNumber: /^[0-9]{4}\s*[0-9]{4}$/,
    sortCode: /^\d{2}-?\d{2}-?\d{2}$/g,
};
const nameValidator = /^(?=[a-zA-Z].*[a-zA-Z])[a-zA-Z'` -]*$/;
const phoneValidator = /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|#)\d{3,4})?$/;
const postcode = /^(GIR 0AA|[A-PR-UWYZ][A-HK-Y]?\d[A-Z\d]?\s?\d[ABD-HJLNP-UW-Z]{2})$/i;
export const integerValidationMessage = "Please enter a whole number.";
const totalPensionFundValidationMessage = "The fund value must be at least £100.00 and no more than £1,000,000.00";
export const postcodeValidator = (value: string) => {
    if (typeof value !== "string") {
        return false;
    }
    // Normalise: trim, uppercase
    const normalised = value
        .trim()
        .toUpperCase();

    return postcode.test(normalised);
};

export const maxDecimalPrecisionValidator = (maxDecimalPlaces: number, fieldName?: string) => {
    return helpers.withMessage(
        `The ${fieldName ? fieldName + " field" : "field"} must have no more than ${maxDecimalPlaces} decimal place${maxDecimalPlaces !== 1 ? "s." : "."}`,
        (value: number) => {
            const decimals = value.toString().split(".")[1];
            if (!decimals) {
                return true;
            }

            return decimals.length <= maxDecimalPlaces;
        },
    );
};

const optionalWaistLimitUpperValidator =
    (maxLimit: number, unitType: MeasurementUnitType, waistUnit: MeasurementUnitType) => (value: number) => {
        return !helpers.req(value) || waistUnit !== unitType || value <= maxLimit;
    };

const optionalWaistLimitLowerValidator =
    (minLimit: number, unitType: MeasurementUnitType, waistUnit: MeasurementUnitType) => (value: number) => {
        return !helpers.req(value) || waistUnit !== unitType || value >= minLimit;
    };

const atLeastOneSmokeTypeValidator = (store: ReturnType<typeof useAboutYouStore> | ReturnType<typeof useDependantAboutYouStore>) => {
    return () => !((store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously) &&
        store.cigarettes === false &&
        store.cigars === false &&
        store.rollingTobacco === false &&
        store.pipeTobacco === false);
};

const protectionDependantRule = (store: ReturnType<typeof usePensionOptionsStore>) => helpers.withMessage("You have selected levels of Spouse Pension and Value Protection which exceed 100% of the value of the pension fund being used, please revise your options before continuing.", () => {
    if (store.valueProtectionPercent && store.spouseDependantPercent) {
        if (parseInt(store.valueProtectionPercent) + parseInt(store.spouseDependantPercent) > 100) {
            return false;
        } else {
            return true;
        }
    }
    return true;
});

export const dobMaxDate = startOfDay(sub(new Date(), { years: 55 }));
export const dobMinDate = startOfDay(sub(new Date(), ({ years: 84, months: 9 })));

export const dobMaxDateDependant = startOfDay(sub(new Date(), { years: 18 }));

const emailValidators = {
    required: helpers.withMessage("The email address field is required", required),
    email: helpers.withMessage("The email address field must be a valid email", helpers.regex(/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,63}$/i)),
};

export const yourDetailsValidator = computed(() => ({
    title: {
        required: helpers.withMessage("The title field is required", required),
    },
    forename: {
        required: helpers.withMessage("The forename field is required", required),
        validateName: helpers.withMessage(`The forename should not contain any numbers or
        punctuation apart from an apostrophe (') or a hyphen (-), and must contain at least two letters.`, helpers.regex(nameValidator)),
    },
    surname: {
        required: helpers.withMessage("The surname field is required", required),
        validateName: helpers.withMessage(`The surname should not contain any numbers or
        punctuation apart from an apostrophe (') or a hyphen (-), and must contain at least two letters.`, helpers.regex(nameValidator)),
    },
    email: emailValidators,
    maritalStatus: {
        required: helpers.withMessage("The marital status field is required", required),
    },
    dob: {
        required: helpers.withMessage("The date of birth field is required", required),
        validDate: helpers.withMessage("Please select a valid date", (value: string) => {
            if (!helpers.req(value !== "string")) {
                return false;
            }
            const thisDate = parseISO(value);
            return (thisDate.toString() !== "Invalid Date");
        }),
        validateAge: helpers.withMessage(`You are not in the required age range.
        To obtain a quote on our site then you must be at least 55 years of age and no older than 84 years and 9 months of age.`, (value: string) => {
            const thisDate = parseISO(value);
            return (thisDate >= dobMinDate && thisDate <= dobMaxDate);
        }),
    },
    phone: {
        validNumber: helpers.withMessage("Invalid number", helpers.regex(phoneValidator)),
    },
    postcode: {
        required: helpers.withMessage("The postcode field is required", required),
        validPostcode: helpers.withMessage("The postcode field must be a valid postcode", postcodeValidator),
    },
}));

export const aboutYouValidator = (store: ReturnType<typeof useAboutYouStore> | ReturnType<typeof useDependantAboutYouStore>) => computed(() => ({
    alcohol: {
        numeric:
         helpers.withMessage("The unit field must not contains letters or special characters.",
             numeric),
        required: helpers.withMessage("The units field is required. " +
            "Please enter the number of units you consume per week, for example 5.",
        required),
        maxValue: helpers.withMessage("The units field must be 999 or less.",
            () => { return Number(store.alcohol) <= 999; }),
        integer: helpers.withMessage(integerValidationMessage, integer),
    },
    heightUnit: {
        required: helpers.withMessage("The height unit field is required.", required),
    },
    heightFeet: {
        requiredIf: helpers.withMessage("The feet field is required.", requiredIf(() => {
            return store.heightUnit === MeasurementUnitType.Imperial;
        }),
        ),
        numeric: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The feet field must not contains letters or special characters.",
                numeric)
            : true,
        maxValue: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The feet field must be 10 or less.", () => {
                return Number(store.heightFeet) <= 10;
            })
            : true,
        minValue: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The feet field must be 1 or more.", () => {
                return Number(store.heightFeet) >= 1;
            })
            : true,
        integer: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The feet field must be a whole number.", () => {
                return Number.isInteger(Number(store.heightFeet));
            })
            : true,
    },
    heightInches: {
        requiredIf: helpers.withMessage("The inches field is required.", requiredIf(() => {
            return store.heightUnit === MeasurementUnitType.Imperial;
        }),
        ),
        numeric: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The inches field must not contains letters or special characters.", numeric)
            : true,
        maxValue: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The inches field must be 11 or less.", () => {
                return Number(store.heightInches) <= 11;
            })
            : true,
        integer: store.heightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The inches field must be a whole number.", () => {
                return Number.isInteger(Number(store.heightInches));
            })
            : true,
    },
    heightCentimetres: {
        requiredIf:
        helpers.withMessage("The centimetres field is required.",
            requiredIf(() => store.heightUnit === MeasurementUnitType.Metric)),
        numeric: store.heightUnit === MeasurementUnitType.Metric
            ? true
            : helpers.withMessage("The centimetres field may only contain numeric characters.", numeric),
        maxValue: store.heightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The centimetre field must be 334 or less.", () =>
                Number(store.heightCentimetres) <= 334)
            : true,
        minValue: store.heightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The centimetre field must be 30 or more.", () =>
                Number(store.heightCentimetres) >= 30)
            : true,
        integer: store.heightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The centimetre field must be a whole number.", () => {
                return Number.isInteger(Number(store.heightCentimetres));
            })
            : true,
    },
    weightUnit: {
        required: helpers.withMessage("The weight unit field is required.", required),
    },
    weightStone: {
        requiredIf: helpers.withMessage("The stone field is required.", requiredIf(() => {
            return store.weightUnit === MeasurementUnitType.Imperial;
        }),
        ),
        numeric: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The stone field may only contain numeric characters.",
                numeric)
            : true,
        maxValue: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The stone field must be 40 or less.", () => {
                return (Number(store.weightStone) <= 40);
            })
            : true,
        minValue: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The stone field must be 4 or more.", () => {
                return Number(store.weightStone) >= 4;
            })
            : true,
        integer: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The stone field must be a whole number.", () => {
                return Number.isInteger(Number(store.weightStone));
            })
            : true,
    },
    weightPounds: {
        requiredIf: helpers.withMessage("The pounds field is required.", requiredIf(() => {
            return store.weightUnit === MeasurementUnitType.Imperial;
        }),
        ),
        numeric: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The pounds field may only contain numeric characters.", numeric)
            : true,
        maxValue: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The pounds field must be 13 or less.", () => {
                return Number(store.weightPounds) <= 13;
            })
            : true,
        integer: store.weightUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The pounds field must be a whole number.", () => {
                return Number.isInteger(Number(store.weightPounds));
            })
            : true,
    },
    weightKg: {
        requiredIf: helpers.withMessage("The kilogram field is required.", requiredIf(() => {
            return store.weightUnit === MeasurementUnitType.Metric;
        }),
        ),
        numeric: store.weightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The kilogram field may only contain numeric characters.", numeric)
            : true,
        maxValue: store.weightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The kilogram field must be 260 or less.", () =>
                Number(store.weightKg) <= 260)
            : true,
        minValue: store.weightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The kilogram field must be 25 or more.", () =>
                Number(store.weightKg) >= 25)
            : true,
        integer: store.weightUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The kilogram field must be a whole number.", () => {
                return Number.isInteger(Number(store.weightKg));
            })
            : true,
    },
    waistInches: {
        required: false,
        numeric: store.waistUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The inches field may only contain numeric characters.", numeric)
            : true,
        maxValue: helpers.withMessage("The inches field must be 80 or less.",
            optionalWaistLimitUpperValidator(80, MeasurementUnitType.Imperial, store.waistUnit)),
        minValue: helpers.withMessage("The inches field must be 12 or more.",
            optionalWaistLimitLowerValidator(12, MeasurementUnitType.Imperial, store.waistUnit)),
        integer: store.waistUnit === MeasurementUnitType.Imperial
            ? helpers.withMessage("The inches field must be a whole number.", () => {
                return Number.isInteger(Number(store.waistInches));
            })
            : true,
    },
    waistCentimetres: {
        required: false,
        numeric: store.waistUnit === MeasurementUnitType.Metric
            ? helpers.withMessage("The centimetre field may only contain numeric characters.", numeric)
            : true,
        maxValue: helpers.withMessage("The centimetre field must be 203 or less.",
            optionalWaistLimitUpperValidator(203, MeasurementUnitType.Metric, store.waistUnit)),
        minValue: helpers.withMessage("The centimetre field must be 30 or more.",
            optionalWaistLimitLowerValidator(30, MeasurementUnitType.Metric, store.waistUnit)),
    },
    medical: { required: helpers.withMessage("The medical field is required.", required) },
    medicalQuantity: {
        requiredIf: helpers.withMessage("You answered \"Yes\" to having medical conditions, but have not declared any.", requiredIf(() => {
            return store.medical === true && store.selectedMedicalConditions.length === 0;
        })),
    },
}));

export const smokerValidator = (store: ReturnType<typeof useAboutYouStore> | ReturnType<typeof useDependantAboutYouStore>) => computed(() => ({
    smoker: {
        required: helpers.withMessage("The smoking field is required.", required),
    },
    smokerType: {
        type: helpers.withMessage("You need to answer 'yes' to at least one type of tobacco product.", atLeastOneSmokeTypeValidator(store)),
    },
    smokingStart: {
        requiredIf: helpers.withMessage("The smoking start field is required.", requiredIf(() =>
            store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously),
        ),
    },
    smokingEnd: {
        requiredIf: helpers.withMessage("The smoking end field is required.", requiredIf(() =>
            store.smoker === SmokerStatus.Previously,
        ),
        ),
        minValue: store.smoker === SmokerStatus.Previously
            ? helpers.withMessage("The stop smoking field must be after the start smoking date.",
                () => isAfter(new Date(store.smokingEnd), new Date(store.smokingStart)))
            : true,
    },
    cigarettes: {
        requiredIf: helpers.withMessage("The cigarettes field is required", requiredIf(() =>
            (store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously)),
        ),
    },
    cigarettesPerDay: {
        requiredIf: helpers.withMessage("The cigarettes field is required. Please enter a whole number, for example 1.", requiredIf(() => {
            return !!store.cigarettes;
        }),
        ),
        maxValue: !store.cigarettes
            ? true
            : helpers.withMessage("The cigarettes field must be 999 or less.", () =>
                Number(store.cigarettesPerDay) <= 999),
        minValue: !store.cigarettes
            ? true
            : helpers.withMessage("The cigarettes field must be 1 or more.", () =>
                Number(store.cigarettesPerDay) >= 1),
        integer: helpers.withMessage(integerValidationMessage, integer),
    },
    cigars: {
        requiredIf: helpers.withMessage("The cigars field is required", requiredIf(() =>
            (store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously)),
        ),
    },
    cigarsPerDay: {
        requiredIf: helpers.withMessage("The cigars field is required. Please enter a whole number, for example 1.", requiredIf(() => {
            return !!store.cigars;
        }),
        ),
        maxValue: !store.cigars
            ? true
            : helpers.withMessage("The cigars field must be 999 or less.", () =>
                Number(store.cigarsPerDay) <= 999),
        minValue: !store.cigars
            ? true
            : helpers.withMessage("The cigars field must be 1 or more.", () =>
                Number(store.cigarsPerDay) >= 1),
        integer: helpers.withMessage(integerValidationMessage, integer),
    },
    rollingTobacco: {
        requiredIf: helpers.withMessage("The tobacco field is required", requiredIf(() =>
            (store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously)),
        ),
    },
    rollingTobaccoPerWeek: {
        requiredIf: helpers.withMessage("The tobacco field is required.", requiredIf(() => {
            return !!store.rollingTobacco;
        }),
        ),
        maxValue: !store.rollingTobacco
            ? true
            : helpers.withMessage("The tobacco field must be 999 or less.", () =>
                Number(store.rollingTobaccoPerWeek) <= 999),
        minValue: !store.rollingTobacco
            ? true
            : helpers.withMessage("The tobacco field must be 1 or more.", () =>
                Number(store.rollingTobaccoPerWeek) >= 1),
        integer: helpers.withMessage(integerValidationMessage, integer),
    },
    pipeTobacco: {
        requiredIf: helpers.withMessage("The pipe tobacco field is required", requiredIf(() =>
            (store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously)),
        ),
    },
    pipeTobaccoPerWeek: {
        requiredIf: helpers.withMessage("The pipe tobacco field is required.", requiredIf(() => {
            return store.pipeTobacco === true;
        }),
        ),
        maxValue: !store.pipeTobacco
            ? true
            : helpers.withMessage("The tobacco field must be 999 or less.",
                () => Number(store.pipeTobaccoPerWeek) <= 999),
        minValue: !store.pipeTobacco
            ? true
            : helpers.withMessage("The tobacco field must be 1 or more.",
                () => Number(store.pipeTobaccoPerWeek) >= 1),
        integer: helpers.withMessage(integerValidationMessage, integer),
    },
}));

// validation rules unique to dependant and not shared with main about you
export const dependantAboutYouValidator = computed(() => ({
    relationshipToCustomer: {
        required: helpers.withMessage("The relationship field is required", required),
    },
    dob: {
        required: helpers.withMessage("The date of birth field is required", required),
        validDate: helpers.withMessage("Please select a valid date", (value: string) => {
            if (!helpers.req(value !== "string")) {
                return false;
            }
            const thisDate = parseISO(value);
            return (thisDate.toString() !== "Invalid Date");
        }),
        validateAge: helpers.withMessage(`Your dependant is not in the required age range.
        Your dependant must be at least 18 years of age and no older than 84 years and 9 months of age.`, (value: string) => {
            const thisDate = parseISO(value);
            return (thisDate >= dobMinDate && thisDate <= dobMaxDateDependant);
        }),
    },
}));

export const pensionOptionsValidator = (store: ReturnType<typeof usePensionOptionsStore>) => computed(() => ({
    pensionType: {
        required: helpers.withMessage("The Pension Type field is required.", required),
    },
    fundTransferred: {
        required: helpers.withMessage("The Personal Pension transferred field is required.", required),
    },
    guaranteeAnnuityRate: {
        required: helpers.withMessage("The Guaranteed Annuity Rate field is required.", required),
    },
    moreThanTwentyFive: {
        required: helpers.withMessage("The more than 25% tax free cash field is required.", required),
    },
    totalFund: {
        required: helpers.withMessage("The total funds field is required", required),
        maxValue: helpers.withMessage("The total funds must be at least £2,000.00 and no more than £1,000,000.00",
            () => { return store.totalFundNumber <= 1000000; }),
        minValue: helpers.withMessage("The total funds must be at least £2,000.00 and no more than £1,000,000.00",
            () => { return store.totalFundNumber >= 2000; }),
    },
    taxFreeSumFull: {
        required: helpers.withMessage("The full amount of the allowed tax free cash field is required.", required),
    },
    taxFreeSumSpecific: {
        requiredIf: helpers.withMessage("The tax free sum field is required.",
            requiredIf(() => {
                return store.taxFreeSumFull === false;
            }),
        ),
        minValue: helpers.withMessage("The tax free sum must be at least £0", () => { return store.taxFreeSumSpecificNumber >= 0; }),
        maxValue: helpers.withMessage(`The tax free sum must be no more than
            £${(store.totalFundNumber / 4).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        () => { return store.taxFreeSumSpecificNumber <= (store.totalFundNumber / 4); }),
        $autoDirty: true,
    },
    annuityExceedMaximum: {
        required: helpers.withMessage("The annuity exceed maximum iLSA limit field is required.", required),
    },
    acknowledgementILSA: {
        requiredIf: helpers.withMessage("The acknowledgement of iLSA field is required.",
            requiredIf(() => {
                return store.annuityExceedMaximum === true;
            }),
        ),
    },
    escalation: {
        required: helpers.withMessage("The annual increase field is required.", required),
    },
    escalationType: {
        requiredIf: helpers.withMessage("The annual increase type field is required.",
            requiredIf(() => {
                return !!store.escalation;
            }),
        ),
    },
    escalationAmount: {
        required: helpers.withMessage("The annual increase rate field is required.",
            requiredIf(() => {
                return store.escalationType === EscalationType.Fixed;
            }),
        ),
    },
    paymentFrequency: {
        requiredIf: helpers.withMessage("The payment frequency field is required.", required),
    },
    paymentArrears: {
        required: helpers.withMessage("The in advance/in arrears income payment field is required.", required),
    },
    proportional: {
        requiredIf: helpers.withMessage("The spouse/dependant cover option field is required.",
            requiredIf(() => {
                return store.paymentFrequency !== PaymentFrequency.Monthly &&
                    store.paymentArrears === PaymentArrears.InArrears;
            }),
        ),
    },
    spouseDependant: {
        required: helpers.withMessage("The proportion cover option field is required.", required),
    },
    spouseDependantPercent: {
        requiredIf: helpers.withMessage("The spouse/dependant percentage field is required.",
            requiredIf(() => {
                return !!store.spouseDependant;
            }),
        ),
        protectionDependant: protectionDependantRule(store),
    },
    valueProtectOrGuarantee: {
        required: helpers.withMessage("The value protection field is required.", required),
    },
    valueProtectionPercent: {
        requiredIf: helpers.withMessage("The Value Protection percent field is required.",
            requiredIf(() => {
                return store.valueProtectOrGuarantee === ValueProtectOrGuarantee.ValueProtection;
            }),
        ),
        protectionDependant: protectionDependantRule(store),
    },
    valueProtectionBasis: {
        requiredIf: helpers.withMessage("The value protection end field is required.",
            requiredIf(() => {
                return store.valueProtectOrGuarantee === ValueProtectOrGuarantee.ValueProtection;
            }),
        ),
    },
    guaranteePeriodYears: {
        requiredIf: helpers.withMessage("The guarantee period field is required.",
            requiredIf(() => {
                return store.valueProtectOrGuarantee === ValueProtectOrGuarantee.GuaranteePeriod;
            }),
        ),
    },
    overlap: {
        requiredIf: helpers.withMessage("The annuity overlap field is required.",
            requiredIf(() => {
                return !!store.spouseDependant &&
                    store.valueProtectOrGuarantee === ValueProtectOrGuarantee.GuaranteePeriod;
            }),
        ),
    },
}));

export const dailyActivitiesValidator = computed(() => ({
    dressYourself: {
        required: helpers.withMessage("The Dress Yourself field is required.", required),
    },
    applicantMobility: {
        required: helpers.withMessage("The Applicant Mobility field is required.", required),
    },
    applicantTransferring: {
        required: helpers.withMessage("The Applicant Transferring field is required.", required),
    },
    applicantBladder: {
        required: helpers.withMessage("The Applicant Bladder field is required.", required),
    },
    applicantBowels: {
        required: helpers.withMessage("The Applicant Bowels field is required.", required),
    },
    applicantBathing: {
        required: helpers.withMessage("The Applicant Bathing field is required.", required),
    },
    applicantFeeding: {
        required: helpers.withMessage("The Applicant Feeding field is required.", required),
    },
    applicantProgression: {
        required: helpers.withMessage("The Applicant Progression field is required.", required),
    },
}));

export const finallyValidator = computed(() => ({
    consent: {
        required: helpers.withMessage("The consent field is required.", required),
    },
}));

export const loginValidator = computed(() => ({
    email: emailValidators,
}));

export const loginConfirmValidator = computed(() => ({
    code: {
        required: helpers.withMessage("The Magic Code field is required.", required),
    },
}));
export const samePostcodeValidator = (postcodeVerify: string | undefined) => {
    return {
        samePostCode: helpers.withMessage(
            "Your address must match the one you supplied on the quote form",
            (value: string) => !postcodeVerify ||
                normalizeText(value) === normalizeText(postcodeVerify ?? "")),
    };
};

const validAddressFieldRegex = /^\s*([A-Za-zÀ-ÖØ-öø-ÿ\-'_0-9,.&/()]{1,}\s*)*$/;

export const addressValidator = computed(() => ({
    line1: {
        required: helpers.withMessage("The Address field is required.", required),
        validAddressField: helpers.withMessage("The Address field contains invalid characters.", helpers.regex(validAddressFieldRegex)),
    },
    city: {
        required: helpers.withMessage("The City field is required.", required),
        validAddressField: helpers.withMessage("The City field contains invalid characters.", helpers.regex(validAddressFieldRegex)),
    },
    county: {
        required: helpers.withMessage("The County field is required.", required),
        validAddressField: helpers.withMessage("The County field contains invalid characters.", helpers.regex(validAddressFieldRegex)),
    },
    postcode: {
        required: helpers.withMessage("The Postcode field is required.", required),
        ukValid: helpers.withMessage("Quotes can only be offered for UK residents.", postcodeValidator),
    },
    country: {
        required: helpers.withMessage("The Country field is required.", required),
        validAddressField: helpers.withMessage("The Country field contains invalid characters.", helpers.regex(validAddressFieldRegex)),
    },
}));

export const applicationDetailsValidator = (store: ReturnType<typeof useApplicationStore>) => computed(() => ({
    ...yourDetailsValidator.value,
    phone: {
        validNumber: helpers.withMessage("invalid number", helpers.regex(phoneValidator)),
        required: helpers.withMessage("The telephone field is required.", required),
    },
    nationalInsurance: {
        required: helpers.withMessage("The National Insurance number field is required.", required),
        validateNIN: helpers.withMessage(`The National Insurance number field must be a valid
            UK National Insurance Number.`, helpers.regex(/^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{1}$/i)),
    },
    doctorsName: {
        required: helpers.withMessage("The doctor's surgery / doctor's name field is required.", required),
    },
    seeMedicalReport: { required: helpers.withMessage("The show medical report field is required.", required) },
    applicantRemainingIlsa: {
        requiredIf: helpers.withMessage("The annuity overlap field is required.",
            requiredIf(() => {
                return !!store.applicantAboutDetails.applicantLifetimeAllowanceTaxProtection;
            }),
        ),
        maxValue: helpers.withMessage("The iLSA amount value must be between 0 and 268,275.",
            () => { return Number(store.applicantAboutDetails.applicantRemainingIlsa) <= 268275; }),
        minValue: helpers.withMessage("The iLSA amount value must be between 0 and 268,275.",
            () => { return Number(store.applicantAboutDetails.applicantRemainingIlsa) >= 0; }),
        $autoDirty: true,
    },
}));

export const pensionFundsValidator = (state: PensionFund) => computed(() => ({
    pensionType: {
        required: helpers.withMessage("The pension type field is required.", required),
    },
    provider: {
        required: helpers.withMessage("The current provider field is required.", required),
    },
    pensionSchemeName: {
        required: helpers.withMessage("The scheme name field is required.", required),
    },
    existingPlanNumber: {
        required: helpers.withMessage("The plan number field is required.", required),
    },
    totalFund: {
        required: helpers.withMessage(
            totalPensionFundValidationMessage, required),
        minValue: helpers.withMessage(totalPensionFundValidationMessage, minValue(100)),
        maxValue: helpers.withMessage(totalPensionFundValidationMessage, maxValue(1000000)),
    },
    fullValue: {
        required: helpers.withMessage("The full value field is required.", required),
    },
    fullValueReason: {
        required: helpers.withMessage("The details of why field is required.", required),
    },
    maxTaxFreeCash: {
        required: helpers.withMessage("The maximum tax free cash field is required.", required),
    },
    taxFreeCashAmount: {
        required: helpers.withMessage("The tax free cash field is required.", required),
        maxValue: helpers.withMessage("The tax free cash field must be less than or equal to the total fund value.", () => {
            return state.taxFreeCashAmount <= state.totalFund;
        }),
    },
}));

export const applicationCompleteAndSendValidator = (store: ReturnType<typeof useApplicationStore>) => computed(() => ({
    accountNumber: {
        required: helpers.withMessage("The account number field is required.", required),
        validAccountNumber: helpers.withMessage("The account number field must be 8 characters.", helpers.regex(bankValidator.accountNumber)),
    },
    sortCode: {
        required: helpers.withMessage("The sort code field is required.", required),
        minValue: helpers.withMessage("The sort code field must be at least 6 characters.",
            () => { return store.paymentInformation.sortCode ? store.paymentInformation.sortCode.toString()?.length >= 6 : true; }),
        validSort: helpers.withMessage("The sort code field must only contain numbers or dashes and have 6 digits in it", helpers.regex(bankValidator.sortCode)),
    },
    bankName: {
        required: helpers.withMessage("The bank name field is required.", required),
        minValue: helpers.withMessage("The bank name field must be at least 2 characters.",
            () => store.paymentInformation.bankName.length >= 2),
    },
    bankBranchName: {
        required: helpers.withMessage("The bank branch name field is required.", required),
        minValue: helpers.withMessage("The bank branch name field must be at least 2 characters.",
            () => store.paymentInformation.bankBranchName.length >= 2),
    },
    accountHolder: {
        required: helpers.withMessage("The account holder's name field is required.", required),
        validateName: helpers.withMessage(`The account holder's name should not contain any numbers
         or punctuation apart from an apostrophe (') or a hyphen (-), and must contain at least two letters.`, helpers.regex(nameValidator)),
    },
    line1: {
        required: helpers.withMessage("The bank address 1 field is required.", required),
        minValue: helpers.withMessage("The bank address 1 field must be at least 2 characters.",
            () => store.paymentInformation.address.line1.length >= 2),
    },
    line2: {
        required: helpers.withMessage("The bank address 2 field is required.", required),
        minValue: helpers.withMessage("The bank address 2 field must be at least 2 characters.",
            () => store.paymentInformation.address.line2.length >= 2),
    },
    city: {
        required: helpers.withMessage("The bank city field is required.", required),
        minValue: helpers.withMessage("The bank city field must be at least 2 characters.",
            () => store.paymentInformation.address.city.length >= 2),
    },
    postcode: {
        required: helpers.withMessage("The postcode field is required.", required),
        ukValid: helpers.withMessage("Payments can only be made to UK banks.", postcodeValidator),
    },
}));

export const quickQuoteValidator = (store: ReturnType<typeof useQuickQuoteCalculatorStore>) => computed(() => ({
    age: {
        required: helpers.withMessage("The age field is required", required),
        maxValue: helpers.withMessage("The age must be at least 55 and no more than 84",
            () => { return (store.age === null ? 0 : store.age) <= 84; }),
        minValue: helpers.withMessage("The age must be at least 55 and no more than 84",
            () => { return (store.age === null ? 0 : store.age) >= 55; }),
    },
    fundValue: {
        required: helpers.withMessage("The fund value field is required", required),
        maxValue: helpers.withMessage("The fund value must be at least £2,000.00 and no more than £1,000,000.00",
            () => { return (store.fundValue === null ? 0 : store.fundValue) <= 1000000; }),
        minValue: helpers.withMessage("The total funds must be at least £2,000.00 and no more than £1,000,000.00",
            () => { return (store.fundValue === null ? 0 : store.fundValue) >= 2000; }),
    },
    postcode: {
        required: helpers.withMessage("The postcode field is required", required),
        validPostcode: helpers.withMessage("The postcode field must be a valid postcode", postcodeValidator),
    },
}));
